import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useDrawerControls } from '@/components/layouts';
import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * The collapsed panel's signals (brief §3, decision 7): a flag with how many
 * details are still to add, and a history icon with a dot while there are
 * changes saved since the panel was last open. Either opens the panel.
 */
export type DetailsRailProps = {
  missingCount: number;
  unseenChanges: boolean;
};

export const DetailsRail = ({ missingCount, unseenChanges }: DetailsRailProps) => {
  const { t } = useTranslation('pmDetails');
  const { expandDrawer } = useDrawerControls();
  const missingLabel =
    missingCount > 0 ? t('panel.ToAdd', { count: missingCount }) : t('panel.Missing');
  return (
    <Stack
      // A fixed 32px: the collapsed drawer's content box has no width of its
      // own, so a relative width would leave the rail 0px wide.
      sx={{
        alignItems: 'center',
        alignSelf: 'center',
        gap: 1,
        mt: 1,
        width: 'var(--input-height-small)',
      }}
      data-automation-id="details-rail"
    >
      <Tooltip title={missingLabel} placement="left">
        <IconButton
          size="small"
          aria-label={missingLabel}
          onClick={expandDrawer}
          sx={{ position: 'relative' }}
          data-automation-id="details-rail-missing"
        >
          <MaterialSymbol name="flag" size={20} />
          {missingCount > 0 ? (
            <Box
              component="span"
              aria-hidden
              data-automation-id="details-rail-missing-count"
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                minWidth: 16,
                height: 16,
                px: 0.5,
                boxSizing: 'border-box',
                borderRadius: 'var(--border-radius-pill)',
                bgcolor: 'var(--color-warning-80)',
                color: 'var(--fg-1)',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 10,
                lineHeight: '16px',
                textAlign: 'center',
              }}
            >
              {missingCount}
            </Box>
          ) : null}
        </IconButton>
      </Tooltip>
      <Tooltip title={t('panel.Changed')} placement="left">
        <IconButton
          size="small"
          aria-label={t('panel.Changed')}
          onClick={expandDrawer}
          sx={{ position: 'relative' }}
          data-automation-id="details-rail-changed"
        >
          <MaterialSymbol name="history" size={20} />
          {unseenChanges ? (
            <Box
              component="span"
              aria-hidden
              data-automation-id="details-rail-changed-dot"
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 8,
                height: 8,
                borderRadius: 'var(--border-radius-pill)',
                bgcolor: 'var(--color-blue-80)',
              }}
            />
          ) : null}
        </IconButton>
      </Tooltip>
    </Stack>
  );
};
